import { useCallback, useEffect, useRef } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query'

import { yakurekiPollKey } from '@/apps/latest/shared/recording-status/usePollEvent'
import {
  useToast,
  type ToastContextValue,
} from '@/shared/contexts/ToastContext'

import {
  deleteNutritionAudio,
  deleteNutritionRecord,
  generateNutritionReport,
  getNutritionReport,
  patchNutritionReport,
  postNutritionAudio,
  type NutritionReport,
  type NutritionRound,
} from './api'
import { isProcessingStage } from './nutritionPhase'

// 報告書の query。1 記録 1 本。
export const nutritionReportKey = (eventId: string) =>
  ['nutritionReport', eventId] as const

// 報告書の取得。記録は報告書の行と一緒に生まれるので、録音前から在る。
// 段階の進みは共有カーネルの polling (usePollEvent) が status で知らせる。
// 報告書の中身と段 (stage) は段階で変わる (文字起こし済みになる・着地する) が、どちらも録音イベントの status の
// 変化と同時に起きる (back は保存してから status を進める) ので、status が変わったら取り直す。
// 処理中のまま止まった記録は status が変わらないので、処理中の段の間だけ 1 分ごとに取り直し、back の判定
// (15 分。pipeline.STALE_PROCESSING_S) で失敗の段に替わったのを知る。
// 捨てずに取り直す (invalidate) ので、取り直している間も前の値が出たまま = 画面が取得中に戻らない。
export const useNutritionReport = ({
  eventId,
  eventStatus,
}: {
  eventId: string | null
  eventStatus: string | undefined
}) => {
  const queryClient = useQueryClient()
  const prevStatus = useRef(eventStatus)
  useEffect(() => {
    if (prevStatus.current === eventStatus) return
    prevStatus.current = eventStatus
    if (eventId)
      void queryClient.invalidateQueries({
        queryKey: nutritionReportKey(eventId),
      })
  }, [eventId, eventStatus, queryClient])
  return useQuery({
    queryKey: nutritionReportKey(eventId ?? ''),
    queryFn: () => getNutritionReport(eventId!),
    enabled: eventId !== null && eventStatus !== undefined,
    // 書き手は自分 (PATCH の応答で手元を更新) と back の段階の進み (status の変化で取り直す) だけなので、
    // 同じ段階の中では時間で古くならない。
    staleTime: Infinity,
    refetchInterval: (query) =>
      isProcessingStage(query.state.data?.stage) ? 60_000 : false,
  })
}

// 段階を進める手 (音声を送る・作る・やり直す・録り直す) の後始末。報告書を取り直し (段が進む。
// 処理中のまま止まった記録のやり直しは status が processing のまま変わらない)、録音イベントを取り直して
// polling を再開させる (usePollEvent は recording / processing の間だけ回る)。回の一覧 (着地の印) も取り直す。
const afterKick = async (queryClient: QueryClient, eventId: string) => {
  void queryClient.invalidateQueries({ queryKey: nutritionReportKey(eventId) })
  await queryClient.invalidateQueries({ queryKey: yakurekiPollKey(eventId) })
  void queryClient.invalidateQueries({ queryKey: ['nutritionRounds'] })
}

const statusOf = (error: unknown) =>
  (error as { response?: { status?: number } })?.response?.status

// 手が失敗したときの toast。409 (処理中・状態が合わない) は back が断った理由をこちらの言葉で、それ以外は 1 行。
const toastOn409 =
  (
    showToast: ToastContextValue['showToast'],
    conflict: string,
    fallback: string
  ) =>
  (error: unknown) =>
    showToast(statusOf(error) === 409 ? conflict : fallback, 'error')

// 録音の停止で全音声を送る (共有 engine の onFallbackCommit)。失敗は投げ返し、engine が控えを
// 握って再送の導線を出す。成功した時点で engine は控えを捨てる。
export const useCommitNutritionAudio = (eventId: string | null) => {
  const queryClient = useQueryClient()
  return useCallback(
    async (blob: Blob, durationSeconds: number): Promise<void> => {
      if (!eventId) throw new Error('eventId missing for audio commit')
      await postNutritionAudio(eventId, { blob, durationSeconds })
      await afterKick(queryClient, eventId)
    },
    [eventId, queryClient]
  )
}

// 文字起こしのやり直し。サーバに置いた音声でもう一度文字起こす (ブラウザに控えは無い)。
export const useRetranscribeNutrition = (eventId: string) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  return useMutation({
    mutationFn: () => postNutritionAudio(eventId),
    onSuccess: () => afterKick(queryClient, eventId),
    onError: toastOn409(
      showToast,
      '処理中か、音声が見つからないためやり直せません',
      '文字起こしをやり直せませんでした'
    ),
  })
}

// 録り直すの 1 手目。録音を捨てて記録を録音前に戻す。録音イベントの取り直しを待ってから resolve するので、
// 呼び出し側はその後にマイクを起動すれば初回と同じ道を通る (完了・失敗の記録のままマイクを起動しない)。
export const useDiscardNutritionAudio = (eventId: string) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  return useMutation({
    mutationFn: () => deleteNutritionAudio(eventId),
    onSuccess: () => afterKick(queryClient, eventId),
    onError: toastOn409(
      showToast,
      '処理中か、報告書があるため録り直せません',
      '録り直せませんでした'
    ),
  })
}

// 記録 (回) を消す (録音・文字起こし・報告書・添付資料・メモごと。元に戻せない)。カルテはそのまま、回の選択から外れる。
// 消した記録の query は捨てる (取り直すと 404 で、開いたままの画面が「見つからない」の戻りに入る)。
export const useDeleteNutritionRecord = (eventId: string) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  return useMutation({
    mutationFn: () => deleteNutritionRecord(eventId),
    onSuccess: () => {
      // 手元の回の一覧からも先に外す。取り直す前の古い一覧のままだと、消した回を開き直して 404 になる
      queryClient.setQueriesData<NutritionRound[]>(
        { queryKey: ['nutritionRounds'] },
        (rounds) => rounds?.filter((r) => r.id !== eventId)
      )
      queryClient.removeQueries({ queryKey: nutritionReportKey(eventId) })
      queryClient.removeQueries({ queryKey: yakurekiPollKey(eventId) })
      void queryClient.invalidateQueries({ queryKey: ['nutritionRounds'] })
      void queryClient.invalidateQueries({ queryKey: ['nutritionCustomers'] })
    },
    onError: toastOn409(
      showToast,
      '文字起こしか作成の途中のため、今は消せません',
      '記録を消せませんでした'
    ),
  })
}

// 作る (= 作り直す)。文字起こし・メモ・添付・前回の報告書から作る。着地していれば全置換
// (お客様の欄は残る)。呼び出し側はメモの保存を待ってから呼ぶ。
export const useGenerateNutritionReport = (eventId: string) => {
  const queryClient = useQueryClient()
  const { showToast } = useToast()
  return useMutation({
    mutationFn: () => generateNutritionReport(eventId),
    onSuccess: () => afterKick(queryClient, eventId),
    onError: toastOn409(
      showToast,
      '文字起こしの途中か、作成中のため作れません',
      '報告書を作れませんでした'
    ),
  })
}

// 報告書を丸ごと返す書き込み (欄の保存・栄養価の計算し直しと 1 手) の共通の口。同じ記録への書き込みは 1 本の列で
// 直列にし (どれも応答で報告書を丸ごと差し替えるので、後から着いた応答が先の書き込みを戻さない)、送る前に取り直し
// (status の変化で走るもの) を止める (書き込みの前に読んだ古い応答が、後から着いて新しい値を戻さない)。
export const nutritionReportWriteScope = (eventId: string) => ({
  id: `nutrition-report:${eventId}`,
})

export const useReplaceNutritionReport = (eventId: string) => {
  const queryClient = useQueryClient()
  return {
    beforeWrite: () =>
      queryClient.cancelQueries({ queryKey: nutritionReportKey(eventId) }),
    replace: (report: NutritionReport) =>
      queryClient.setQueryData<NutritionReport>(
        nutritionReportKey(eventId),
        (old) => (old ? report : old)
      ),
  }
}

// 1 欄の保存。触ったキーだけを送り、 応答 (全欄) で手元を更新する。
export const useSaveNutritionReportField = (eventId: string) => {
  const { beforeWrite, replace } = useReplaceNutritionReport(eventId)
  const { showToast } = useToast()
  return useMutation({
    scope: nutritionReportWriteScope(eventId),
    mutationFn: ({ key, value }: { key: string; value: string }) =>
      patchNutritionReport(eventId, { [key]: value }),
    onMutate: beforeWrite,
    onSuccess: replace,
    onError: () => {
      showToast('保存に失敗しました', 'error')
    },
  })
}
